import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setFilterField } from '../../app/itemGridPreferencesSlice';
import { Panel, PanelHeader, PanelBody } from '../../components/panel/panel';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import Preloader from '../../components/CustomComponents/preLoad/preLoader';
import showErrorToast, { showSuccessToast } from '../../components/CustomComponents/toast/toast';
import useThemeColor from '../../hooks/useThemeColor';
import SimpleDateRangePicker from '../../components/CustomComponents/SimpleDateRangePicker/SimpleDateRangePicker';
import DateRangePicker from '../../components/CustomComponents/DateRangePicker/DateRangePicker';
import moment from 'moment';
import Select from 'react-select';
import useDarkMode from '../../hooks/useDarkMode';
import DropDownStyles, { DropDownStylesDark } from './dropdownStyles';
import { defaultSelectPortalProps } from '../../utils/selectUtils';
import CustomModal from '../../components/CustomComponents/modal/customModal';
import swal from 'sweetalert';
import '../order/ManageOrderFilters.css';

const parseBooleanFlag = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
};

const pickValueIgnoreCase = (obj, candidates) => {
  if (!obj) return undefined;
  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return obj[key];
    }
  }

  const lowerCaseMap = Object.keys(obj).reduce((acc, key) => {
    acc[key.toLowerCase()] = obj[key];
    return acc;
  }, {});

  for (const key of candidates) {
    const lowerKey = key.toLowerCase();
    if (lowerCaseMap.hasOwnProperty(lowerKey)) {
      return lowerCaseMap[lowerKey];
    }
  }

  return undefined;
};

const getNumberValue = (record = {}, keys = [], defaultValue = 0) => {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') {
      const num = Number(value);
      if (!Number.isNaN(num)) {
        return num;
      }
    }
  }
  return defaultValue;
};

const normalizeUnpaidHours = (record = {}) => {
  return {
    userName: record.UserName || record.userName || '',
    firstName: record.FirstName || record.firstName || '',
    lastName: record.LastName || record.lastName || '',
    email: record.Email || record.email || '',
    baseRate: getNumberValue(record, ['baseRate', 'BaseRate', 'RatePerHour', 'ratePerHour']),
    totalRegularHours: getNumberValue(record, ['totalRegularHours', 'TotalRegularHours']),
    totalOvertimeHours: getNumberValue(record, ['totalOvertimeHours', 'TotalOvertimeHours']),
    totalBreakHours: getNumberValue(record, ['totalBreakHours', 'TotalBreakHours']),
    unpaidBreakHours: getNumberValue(record, ['unpaidBreakHours', 'UnpaidBreakHours']),
    paidBreakHours: getNumberValue(record, ['paidBreakHours', 'PaidBreakHours']),
    extraBreakHours: getNumberValue(record, ['extraBreakHours', 'ExtraBreakHours']),
    adjustedPaidBreakHours: getNumberValue(record, ['adjustedPaidBreakHours', 'AdjustedPaidBreakHours']),
    totalWorkingHours: getNumberValue(record, ['totalWorkingHours', 'TotalWorkingHours']),
    regularEarnings: getNumberValue(record, ['regularEarnings', 'RegularEarnings']),
    overtimeEarnings: getNumberValue(record, ['overtimeEarnings', 'OvertimeEarnings']),
    paidBreakEarnings: getNumberValue(record, ['paidBreakEarnings', 'PaidBreakEarnings']),
    totalEarnings: getNumberValue(record, ['totalEarnings', 'TotalEarnings']),
    entryCount: getNumberValue(record, ['entryCount', 'EntryCount']),
    firstShiftDate: record.FirstShiftDate || record.firstShiftDate || null,
    lastShiftDate: record.LastShiftDate || record.lastShiftDate || null,
  };
};

const computeAdjustedUnpaidHours = (normalized, staffProfile) => {
  if (!normalized) return null;

  const isSalaryStaff = Boolean(staffProfile?.isSalaryStaff);
  const totalRegularHours = normalized.totalRegularHours || 0;
  const totalOvertimeHours = normalized.totalOvertimeHours || 0;
  const unpaidBreakHours = normalized.unpaidBreakHours || 0;
  const paidBreakHours = normalized.paidBreakHours || 0;
  const extraBreakHours = normalized.extraBreakHours || 0;
  // Use adjusted paid break hours from API (already has extra breaks subtracted)
  const adjustedPaidBreakHours = normalized.adjustedPaidBreakHours ?? (paidBreakHours > 0 && extraBreakHours > 0 ? Math.max(0, paidBreakHours - extraBreakHours) : paidBreakHours);
  const baseRate = staffProfile?.ratePerHour ?? normalized.baseRate ?? 0;
  const overtimeRateFallback = staffProfile?.overTimeHourlyRate || baseRate;

  const regularRate =
    totalRegularHours > 0
      ? (normalized.regularEarnings || 0) / totalRegularHours
      : baseRate;

  const overtimeRate =
    totalOvertimeHours > 0
      ? (normalized.overtimeEarnings || 0) / totalOvertimeHours
      : overtimeRateFallback || regularRate;

  let adjustedRegularHours = totalRegularHours;
  let adjustedOvertimeHours = totalOvertimeHours;
  let remainingDeduction = isSalaryStaff ? 0 : unpaidBreakHours;

  // For hourly staff: deduct extra break hours from payable hours
  if (!isSalaryStaff && extraBreakHours > 0) {
    remainingDeduction += extraBreakHours;
  }

  if (remainingDeduction > 0 && adjustedRegularHours > 0) {
    const deduction = Math.min(remainingDeduction, adjustedRegularHours);
    adjustedRegularHours -= deduction;
    remainingDeduction -= deduction;
  }

  if (remainingDeduction > 0 && adjustedOvertimeHours > 0) {
    const deduction = Math.min(remainingDeduction, adjustedOvertimeHours);
    adjustedOvertimeHours -= deduction;
    remainingDeduction -= deduction;
  }

  // For earnings: add adjusted paid break hours to regular hours
  // Use paid break earnings from API (already calculated with adjusted hours) or calculate it
  const adjustedPaidBreakEarnings = normalized.paidBreakEarnings || (adjustedPaidBreakHours * regularRate);
  const payableRegularHoursWithBreaks = adjustedRegularHours + adjustedPaidBreakHours;
  const payableRegularEarnings = payableRegularHoursWithBreaks * regularRate;
  const payableOvertimeEarnings = adjustedOvertimeHours * overtimeRate;
  const payableWorkingEarnings = payableRegularEarnings + payableOvertimeEarnings;
  // Total earnings includes regular (with paid breaks) + overtime
  const payableTotalEarnings = payableWorkingEarnings;

  return {
    ...normalized,
    regularRate,
    overtimeRate,
    payableRegularHours: adjustedRegularHours,
    payableOvertimeHours: adjustedOvertimeHours,
    payableHours: adjustedRegularHours + adjustedOvertimeHours,
    payableRegularEarnings,
    payableOvertimeEarnings,
    payableWorkingEarnings,
    payableTotalEarnings,
    unpaidBreakAdjustmentHours: isSalaryStaff ? 0 : unpaidBreakHours,
    adjustedPaidBreakHours,
    adjustedPaidBreakEarnings,
    extraBreakHours,
  };
};

const StaffReport = () => {
  const pageKey = 'staffWorkReport';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const currentUserName = useSelector((state) => state.auth.user?.userData?.userName);
  const { profileData } = useSelector((state) => state.auth);
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;

  // Get filter preferences from Redux
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  
  // Default filter values - use useMemo to prevent recreation on every render
  const defaultDateRange = useMemo(() => [
    moment().startOf('week').toDate(),
    moment().endOf('week').toDate(),
  ], []);
  const defaultSelectedStaff = useMemo(() => ({ value: '', label: 'All Staff' }), []);
  const defaultSearch = '';
  const defaultLocation = useMemo(() => ({ value: 'all', label: 'All Locations' }), []);
  const parentIdForLocation = isMain && CompID != null ? (typeof CompID === 'number' ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? 'Parent Branch';

  // Get filter values from Redux, with fallback to defaults
  const dateRange = preferences.dateRange || defaultDateRange;
  
  // Memoize date parsing to prevent creating new Date objects on every render
  const parseDate = useCallback((dateValue, defaultDate) => {
    if (!dateValue) return defaultDate;
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? defaultDate : dateValue;
    }
    const parsed = new Date(dateValue);
    return isNaN(parsed.getTime()) ? defaultDate : parsed;
  }, []);
  
  // Get date timestamps for comparison (to detect actual date changes, not object reference changes)
  // Use JSON.stringify to compare the actual date values, not object references
  const dateRangeKey = useMemo(() => {
    if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
      return JSON.stringify([defaultDateRange[0].getTime(), defaultDateRange[1].getTime()]);
    }
    const start = dateRange[0] instanceof Date ? dateRange[0].getTime() : (dateRange[0] ? new Date(dateRange[0]).getTime() : defaultDateRange[0].getTime());
    const end = dateRange[1] instanceof Date ? dateRange[1].getTime() : (dateRange[1] ? new Date(dateRange[1]).getTime() : defaultDateRange[1].getTime());
    return JSON.stringify([start, end]);
  }, [dateRange, defaultDateRange]);
  
  const startDateTimestamp = useMemo(() => {
    const date = dateRange?.[0];
    if (!date) return defaultDateRange[0].getTime();
    if (date instanceof Date) return date.getTime();
    return new Date(date).getTime();
  }, [dateRangeKey, defaultDateRange]);
  
  const endDateTimestamp = useMemo(() => {
    const date = dateRange?.[1];
    if (!date) return defaultDateRange[1].getTime();
    if (date instanceof Date) return date.getTime();
    return new Date(date).getTime();
  }, [dateRangeKey, defaultDateRange]);
  
  // Memoize dates so they only change when the actual date values change
  const startDate = useMemo(() => {
    return parseDate(dateRange[0], defaultDateRange[0]);
  }, [startDateTimestamp, parseDate, defaultDateRange]);
  
  const endDate = useMemo(() => {
    return parseDate(dateRange[1], defaultDateRange[1]);
  }, [endDateTimestamp, parseDate, defaultDateRange]);
  
  // Memoize selectedStaff to prevent unnecessary re-renders
  const selectedStaff = useMemo(() => {
    return preferences.selectedStaff || defaultSelectedStaff;
  }, [preferences.selectedStaff, defaultSelectedStaff]);
  
  const search = preferences.search || defaultSearch;
  const location = preferences.location || defaultLocation;

  // Use ref to track if filters have been initialized to prevent multiple initializations
  const filtersInitialized = useRef(false);

  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [staffReports, setStaffReports] = useState([]);
  const [allStaffList, setAllStaffList] = useState([]);
  const [compCurrency, setCompCurrency] = useState("GBP");
  const [showModal, setShowModal] = useState(false);
  const [selectedStaffDetail, setSelectedStaffDetail] = useState(null);
  const [detailReportData, setDetailReportData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutStaff, setPayoutStaff] = useState(null);
  const [rawUnpaidHours, setRawUnpaidHours] = useState(null);
  const [staffProfile, setStaffProfile] = useState(null);
  const [staffLeaves, setStaffLeaves] = useState([]);
  const [selectedLeaves, setSelectedLeaves] = useState(new Set());
  const [loadingUnpaidHours, setLoadingUnpaidHours] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    startDate: moment().startOf('week').toDate(),
    endDate: moment().endOf('week').toDate(),
    payoutType: 'WEEKLY',
    bonusAmount: '',
    deductionAmount: '',
    taxAmount: '',
    notes: ''
  });

  const unpaidHours = useMemo(
    () => computeAdjustedUnpaidHours(rawUnpaidHours, staffProfile),
    [rawUnpaidHours, staffProfile]
  );

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    PKR: "₨",
    Rs: "₨", // Handle legacy "Rs" code
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Normalize currency code (handle legacy "Rs" -> "PKR")
  const normalizeCurrencyCode = (currencyCode) => {
    if (currencyCode === "Rs") return "PKR";
    return currencyCode || "GBP";
  };

  useEffect(() => {
    // Fetch company currency from profile data
    if (profileData?.compCurrency) {
      const normalizedCurrency = normalizeCurrencyCode(profileData.compCurrency);
      setCompCurrency(normalizedCurrency);
    }
  }, [profileData]);

  useEffect(() => {
    fetchStaffList();
  }, []);

  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID },
      });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === 'number' ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? 'Parent Branch';
      const base = [{ value: parentId, label: compName }];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === 'number' ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === 'number' ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          if (pidNum === parentId && idNum !== parentId) return true;
          return false;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? '').trim() || `Branch ${id ?? ''}`;
          const val = typeof id === 'number' ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);
      setLocations([...base, ...branchOpts]);
    } catch (error) {
      console.error('Error fetching locations:', error);
      showErrorToast(error.response?.data?.message || 'Error loading locations');
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, profileData?.compName, profileData?.CompName]);

  const allLocationsOption = { value: 'all', label: 'All Locations' };
  const locationOptions = locations.length > 0
    ? [allLocationsOption, ...locations]
    : parentIdForLocation != null && !isNaN(parentIdForLocation)
      ? [allLocationsOption, { value: parentIdForLocation, label: companyName }]
      : [allLocationsOption];

  // Initialize default filters in Redux if they don't exist (only once on mount)
  useEffect(() => {
    if (filtersInitialized.current) return; // Already initialized
    
    const hasDateRange = preferences.dateRange && Array.isArray(preferences.dateRange) && preferences.dateRange.length === 2;
    const hasSelectedStaff = preferences.selectedStaff && typeof preferences.selectedStaff === 'object';
    const hasSearch = preferences.search !== undefined;
    const hasLocation = preferences.location != null;
    
    if (!hasDateRange) {
      dispatch(setFilterField({ pageKey, field: 'dateRange', value: defaultDateRange }));
    }
    if (!hasSelectedStaff) {
      dispatch(setFilterField({ pageKey, field: 'selectedStaff', value: defaultSelectedStaff }));
    }
    if (!hasSearch) {
      dispatch(setFilterField({ pageKey, field: 'search', value: defaultSearch }));
    }
    if (!hasLocation && isMain) {
      dispatch(setFilterField({ pageKey, field: 'location', value: defaultLocation }));
    }
    
    filtersInitialized.current = true; // Mark as initialized
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    if (isMain) fetchLocations();
  }, [isMain, fetchLocations]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchStaffReports();
    }, 500);
    return () => clearTimeout(handler);
  }, [startDate, endDate, selectedStaff, search, location]);

  const fetchStaffList = async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: { 
          CompID,
          SortBy: 'First_Name',
          SortOrder: 'ASC',
          Search: '',
          PageSize: 1000,
          PageNumber: 1
        }
      });
      
      console.log('Staff List API Response:', response.data); // Debug log
      
      const staffData = response.data.data || response.data || [];
      console.log('Staff List Data:', staffData); // Debug log
      
      if (Array.isArray(staffData)) {
        console.log('Setting staff list, count:', staffData.length); // Debug log
        setAllStaffList(staffData);
      } else {
        console.log('Staff data is not an array'); // Debug log
        setAllStaffList([]);
      }
    } catch (err) {
      showErrorToast('Failed to fetch staff list');
      console.error('Error fetching staff list:', err);
      setAllStaffList([]);
    }
  };

  const fetchStaffReports = async () => {
    if (!CompID) return;

    setLoading(true);
    try {
      // Build params object, only include userName if it has a value
      const params = {
        compID: CompID,
        startDate: startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : '',
        endDate: endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY-MM-DD') : ''
      };

      // Only add userName if a specific staff is selected
      if (selectedStaff?.value) {
        params.userName = selectedStaff.value;
      }
      if (isMain && location?.value !== 'all' && location?.value != null) {
        params.locationFilter = typeof location.value === 'number' ? location.value : parseInt(location.value, 10);
      }

      const response = await axiosInstance.get(APIs.GET_ALL_STAFF_WORK_REPORTS, {
        params: params
      });

      if (response.data && response.data.staffReports) {
        console.log('Staff Reports Response:', response.data.staffReports); // Debug log
        
        let reports = response.data.staffReports.map(staff => {
          console.log('Processing staff (raw):', staff); // Debug log
          
          // ASP.NET Core returns camelCase by default
          const mappedStaff = {
            staff: {
              UserName: staff.userName || staff.UserName || '',
              FirstName: staff.firstName || staff.FirstName || '',
              LastName: staff.lastName || staff.LastName || '',
              Email: staff.email || staff.Email || '',
              RoleName: staff.roleName || staff.RoleName || '',
              UserImage: staff.userImage || staff.UserImage || null,
              ISFullTimeStaff: staff.isFullTimeStaff !== undefined ? staff.isFullTimeStaff : staff.ISFullTimeStaff,
              isFullTimeStaff: staff.isFullTimeStaff !== undefined ? staff.isFullTimeStaff : staff.ISFullTimeStaff,
              RatePerHour: staff.ratePerHour !== undefined ? staff.ratePerHour : staff.RatePerHour,
              IsSalaryStaff: staff.isSalaryStaff !== undefined ? staff.isSalaryStaff : staff.IsSalaryStaff,
              isSalaryStaff: staff.isSalaryStaff !== undefined ? staff.isSalaryStaff : staff.IsSalaryStaff,
              SalaryType: staff.salaryType || staff.SalaryType || '',
              salaryType: staff.salaryType || staff.SalaryType || '',
              Salary: staff.salary !== undefined ? staff.salary : staff.Salary,
              salary: staff.salary !== undefined ? staff.salary : staff.Salary
            },
            report: {
              WeekStats: {
                DaysWorked: staff.daysWorked || staff.DaysWorked || 0,
                RegularHours: staff.regularHours || staff.RegularHours || 0,
                OvertimeHours: staff.overtimeHours || staff.OvertimeHours || 0,
                TotalBreakHours: staff.totalBreakHours || staff.TotalBreakHours || 0,
                UnpaidBreakHours: staff.unpaidBreakHours || staff.UnpaidBreakHours || 0,
                TotalHours: staff.totalHours || staff.TotalHours || 0,
                TotalEarnings: staff.totalEarnings || staff.TotalEarnings || 0
              },
              EntryCount: staff.entryCount || staff.EntryCount || 0
            }
          };
          
          console.log('Mapped staff:', mappedStaff.staff); // Debug log
          return mappedStaff;
        });

        // Apply search filter on frontend if needed
        if (search.trim()) {
          reports = reports.filter(({ staff }) => 
            (staff.FirstName || staff.firstName || '')?.toLowerCase().includes(search.toLowerCase()) ||
            (staff.LastName || staff.lastName || '')?.toLowerCase().includes(search.toLowerCase()) ||
            (staff.Email || staff.email || '')?.toLowerCase().includes(search.toLowerCase())
          );
        }

        setStaffReports(reports);
      } else {
        setStaffReports([]);
      }
    } catch (err) {
      showErrorToast('Failed to fetch staff reports');
      console.error('Error fetching staff reports:', err);
      setStaffReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (update) => {
    const [newStartDate, newEndDate] = update;
    if (
      (!newStartDate || moment(newStartDate).isValid()) &&
      (!newEndDate || moment(newEndDate).isValid())
    ) {
      // Save to Redux
      dispatch(setFilterField({ pageKey, field: 'dateRange', value: [newStartDate, newEndDate] }));
    } else {
      showErrorToast('Invalid date selected');
    }
  };

  const clearFilters = () => {
    // Reset all filters to defaults in Redux
    dispatch(setFilterField({ pageKey, field: 'dateRange', value: defaultDateRange }));
    dispatch(setFilterField({ pageKey, field: 'selectedStaff', value: defaultSelectedStaff }));
    dispatch(setFilterField({ pageKey, field: 'search', value: defaultSearch }));
  };

  const handleStaffChange = (selectedOption) => {
    // Save to Redux
    dispatch(setFilterField({ pageKey, field: 'selectedStaff', value: selectedOption }));
  };

  const handleLocationChange = (option) => {
    dispatch(setFilterField({ pageKey, field: 'location', value: option || defaultLocation }));
  };

  const handleSearchChange = (value) => {
    // Save to Redux
    dispatch(setFilterField({ pageKey, field: 'search', value }));
  };


  const handleViewDetails = (userName) => {
    // Find the staff member from staffReports
    const staffMember = staffReports.find(({ staff }) => 
      (staff.UserName || staff.userName) === userName
    );
    
    if (staffMember) {
      setSelectedStaffDetail(staffMember);
      setShowModal(true);
      setExpandedDays(new Set()); // Reset expanded days
      fetchDetailedReport(userName);
    }
  };

  const fetchDetailedReport = async (userName) => {
    setDetailLoading(true);
    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_WORK_REPORT, {
        params: {
          userName: userName,
          compID: CompID,
          startDate: startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : '',
          endDate: endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY-MM-DD') : ''
        }
      });

      const data = response.data?.data || response.data;
      if (data) {
        setDetailReportData(data);
      }
    } catch (err) {
      showErrorToast('Failed to fetch detailed report');
      console.error('Error fetching detailed report:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleDayExpansion = (date) => {
    const newExpanded = new Set(expandedDays);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDays(newExpanded);
  };


  // Fetch staff profile
  const fetchStaffProfile = useCallback(async (userName) => {
    if (!CompID || !userName) return;

    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_DATA, {
        params: {
          CompID: CompID,
          UserName: userName
        }
      });

      const data = response.data?.data || response.data;
      if (data) {
        setStaffProfile(Array.isArray(data) ? data[0] : data);
      }
    } catch (err) {
      console.error('Error fetching staff profile:', err);
    }
  }, [CompID]);

  // Fetch staff leaves
  const fetchStaffLeaves = useCallback(async (userName) => {
    if (!CompID || !userName) return;
    
    try {
      const response = await axiosInstance.get(APIs.GET_STAFF_LEAVES, {
        params: {
          compId: CompID,
          userName: userName,
          startDate: payoutForm.startDate ? moment(payoutForm.startDate).format('YYYY-MM-DD') : null,
          endDate: payoutForm.endDate ? moment(payoutForm.endDate).format('YYYY-MM-DD') : null,
        },
      });

      const data = response.data?.data || response.data || [];
      const normalizedLeaves = Array.isArray(data)
        ? data.filter(Boolean)
            .filter(leave => !leave.Payout_ID && !leave.payout_ID) // Exclude leaves already in a payout
            .map((leave) => ({
            SL_ID: leave.sL_ID || leave.SL_ID,
            UserName: leave.UserName || leave.userName || '',
            LeaveType: leave.LeaveType || leave.leaveType || '',
            StartDate: leave.StartDate || leave.startDate,
            EndDate: leave.EndDate || leave.endDate,
            TotalHours: leave.TotalHours || leave.totalHours || 0,
            IsPaid: leave.IsPaid ?? leave.isPaid ?? false,
            Reason: leave.Reason || leave.reason || '',
          }))
        : [];

      setStaffLeaves(normalizedLeaves);
      // Always auto-select paid leaves for both salary and hourly staff
      if (staffProfile) {
        const paidLeaveIds = new Set(
          normalizedLeaves.filter(l => l.IsPaid).map(l => l.SL_ID)
        );
        setSelectedLeaves(paidLeaveIds);
      }
    } catch (error) {
      console.error('Error fetching staff leaves:', error);
      setStaffLeaves([]);
    }
  }, [CompID, payoutForm.startDate, payoutForm.endDate, staffProfile?.isSalaryStaff]);

  // Fetch unpaid hours for payout generation
  const fetchUnpaidHours = async (userName) => {
    setLoadingUnpaidHours(true);
    try {
      const response = await axiosInstance.get(APIs.GET_UNPAID_HOURS, {
        params: {
          compID: CompID,
          userName: userName,
          startDate: moment(payoutForm.startDate).format('YYYY-MM-DD'),
          endDate: moment(payoutForm.endDate).format('YYYY-MM-DD')
        }
      });
      
      const data = response.data?.data || response.data || [];
      if (data.length > 0) {
        const unpaidData = data[0];
        const normalized = normalizeUnpaidHours(unpaidData);
        setRawUnpaidHours(normalized);
      } else {
        setRawUnpaidHours(null);
      }
    } catch (error) {
      console.error('Error fetching unpaid hours:', error);
      setRawUnpaidHours(null);
    } finally {
      setLoadingUnpaidHours(false);
    }
  };

  // Handle opening payout modal
  const handleOpenPayoutModal = (staff) => {
    setPayoutStaff(staff);
    setShowPayoutModal(true);
    
    // Initialize with current week dates
    const weekStart = moment().startOf('week').toDate();
    const weekEnd = moment().endOf('week').toDate();
    
    setPayoutForm({
      startDate: weekStart,
      endDate: weekEnd,
      payoutType: 'WEEKLY',
      bonusAmount: '',
      deductionAmount: '',
      taxAmount: '',
      notes: ''
    });
    
    const userName = staff.UserName || staff.userName;
    fetchStaffProfile(userName);
    fetchUnpaidHours(userName);
  };

  // Handle payout form date change
  useEffect(() => {
    if (showPayoutModal && payoutStaff) {
      const userName = payoutStaff.UserName || payoutStaff.userName;
      fetchUnpaidHours(userName);
      fetchStaffLeaves(userName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutForm.startDate, payoutForm.endDate, showPayoutModal]);

  // Fetch leaves when staffProfile becomes available
  useEffect(() => {
    if (showPayoutModal && payoutStaff && staffProfile) {
      const userName = payoutStaff.UserName || payoutStaff.userName;
      fetchStaffLeaves(userName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPayoutModal, staffProfile]);

  // Handle generate payout
  const handleGeneratePayout = async () => {
    if (!unpaidHours || unpaidHours.payableHours <= 0) {
      showErrorToast('No unpaid hours found for the selected period');
      return;
    }

    // Calculate leave adjustment
    let leaveAdjustment = 0;
    let includedLeaveIds = [];
    
    if (staffProfile?.isSalaryStaff) {
      // For salary staff: always include paid leaves, and include selected unpaid leaves
      const paidLeavesList = staffLeaves.filter(l => l.IsPaid);
      const selectedUnpaidLeaves = staffLeaves.filter(l => !l.IsPaid && selectedLeaves.has(l.SL_ID));
      const allLeavesToProcess = [...paidLeavesList, ...selectedUnpaidLeaves];
      
      // Calculate daily salary rate
      const daysInPeriod = moment(payoutForm.endDate).diff(moment(payoutForm.startDate), 'days') + 1;
      const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);
      
      allLeavesToProcess.forEach(leave => {
        const leaveDays = moment(leave.EndDate).diff(moment(leave.StartDate), 'days') + 1;
        const proRatedAmount = dailySalary * leaveDays;
        
        if (leave.IsPaid) {
          // PAID leaves: Don't add anything (already included in base salary)
          // leaveAdjustment remains 0 for paid leaves
        } else {
          // UNPAID leaves: Subtract from salary (negative adjustment)
          leaveAdjustment -= proRatedAmount;
        }
        includedLeaveIds.push(leave.SL_ID);
      });
    } else {
      // For hourly staff: include all paid leave hours
      const paidLeaves = staffLeaves.filter(l => l.IsPaid);
      const hourlyRate = staffProfile?.ratePerHour || 0;
      paidLeaves.forEach(leave => {
        leaveAdjustment += (leave.TotalHours || 0) * hourlyRate;
        includedLeaveIds.push(leave.SL_ID);
      });
    }

    try {
      setLoading(true);
      
      if (!payoutForm.startDate || !payoutForm.endDate) {
        showErrorToast('Please select a valid pay period');
        return;
      }
      
      const startDate = moment(payoutForm.startDate).format('YYYY-MM-DD');
      const endDate = moment(payoutForm.endDate).format('YYYY-MM-DD');
      
      const response = await axiosInstance.post(APIs.GENERATE_PAYOUT, {
        compID: CompID,
        userName: payoutStaff.UserName || payoutStaff.userName,
        startDate: startDate,
        endDate: endDate,
        payoutType: payoutForm.payoutType,
        bonusAmount: parseFloat(payoutForm.bonusAmount) || 0,
        deductionAmount: parseFloat(payoutForm.deductionAmount) || 0,
        taxAmount: parseFloat(payoutForm.taxAmount) || 0,
        notes: payoutForm.notes || '',
        createdBy: currentUserName,
        // Add leave information - send as comma-separated string
        includedLeaveIds: includedLeaveIds.length > 0 ? includedLeaveIds.join(',') : null,
        leaveAdjustmentAmount: leaveAdjustment
      });

      if (response.data?.status) {
        showSuccessToast('Payout generated successfully!');
        setShowPayoutModal(false);
        setPayoutStaff(null);
        setRawUnpaidHours(null);
        setStaffProfile(null);
        setStaffLeaves([]);
        setSelectedLeaves(new Set());
        setPayoutForm({
          startDate: moment().startOf('week').toDate(),
          endDate: moment().endOf('week').toDate(),
          payoutType: 'WEEKLY',
          bonusAmount: '',
          deductionAmount: '',
          taxAmount: '',
          notes: ''
        });
      } else {
        showErrorToast(response.data?.message || 'Failed to generate payout');
      }
    } catch (error) {
      console.error('Error generating payout:', error);
      showErrorToast(error.response?.data?.message || 'Error generating payout');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to payout history page
  const handleViewPayoutHistory = (userName) => {
    navigate(`/team/payouts/${userName}`);
  };

  // Helper functions for modal
  const getEntryIcon = (entry) => {
    if (entry.isBreak) {
      return 'fa fa-coffee';
    } else if (entry.isOvertime) {
      return 'fa fa-arrow-trend-up';
    } else {
      return 'fa fa-clock';
    }
  };

  const getEntryBadgeClass = (entry) => {
    if (entry.isBreak) {
      return entry.breakType === 'UNPAID' ? 'bg-warning' : 'bg-success';
    } else if (entry.isOvertime) {
      return 'bg-purple';
    } else {
      return 'bg-primary';
    }
  };

  const getEntryTypeLabel = (entry) => {
    if (entry.isBreak) {
      return `${entry.breakType} Break`;
    } else if (entry.isOvertime) {
      return 'Overtime';
    } else {
      return 'Regular Shift';
    }
  };

  const getEntryPaidStatus = (entry) => {
    if (!entry) return false;

    const interpretBooleanLike = (value) => {
      if (value === undefined || value === null) return null;
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') {
        if (Number.isNaN(value)) return null;
        return value > 0;
      }
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (!normalized) return null;
        if (['true', '1', 'yes', 'y', 'paid', 'complete', 'completed', 'processed'].includes(normalized)) {
          return true;
        }
        if (['false', '0', 'no', 'n', 'unpaid', 'pending', 'notpaid', 'not-paid', 'open', 'outstanding'].includes(normalized)) {
          return false;
        }
        const numericValue = Number(normalized);
        if (!Number.isNaN(numericValue)) {
          return numericValue > 0;
        }
        return null;
      }
      return null;
    };

    const considerValues = (values = []) => {
      let hasFalse = false;
      for (const value of values) {
        const interpreted = interpretBooleanLike(value);
        if (interpreted === true) {
          return { truthy: true, hasFalse };
        }
        if (interpreted === false) {
          hasFalse = true;
        }
      }
      return { truthy: false, hasFalse };
    };

    const paidFlags = considerValues([
      entry.isPaid, entry.is_paid, entry.isPaidEntry, entry.is_Paid, entry.IsPaid, entry.Is_Paid
    ]);
    if (paidFlags.truthy) return true;

    const statusFlags = considerValues([
      entry.paidStatus, entry.paymentStatus, entry.paid_status, entry.payment_status
    ]);
    if (statusFlags.truthy) return true;

    const payoutRefs = [
      entry.payoutId, entry.payoutID, entry.payout_id, entry.payout_ID,
      entry.PayoutId, entry.PayoutID, entry.Payout_ID,
      entry.payoutUUID, entry.payoutUuid, entry.PayoutUUID, entry.Payout_Uuid
    ];
    const payoutFlags = considerValues(payoutRefs);
    if (payoutFlags.truthy) return true;

    const paidDateValues = [
      entry.paidDateTime, entry.paid_date_time, entry.paidDate,
      entry.PaidDate, entry.PaidDateTime, entry.Paid_DateTime
    ];
    const hasPaidDate = paidDateValues.some((value) => value !== undefined && value !== null && `${value}`.trim() !== '');
    if (hasPaidDate) return true;

    if (paidFlags.hasFalse || statusFlags.hasFalse || payoutFlags.hasFalse) {
      return false;
    }

    return false;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const toNumber = (value, defaultValue = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : defaultValue;
  };

  const resolveNormalRate = (entry, dayRecord = {}, staffData = null) => {
    return toNumber(
      entry?.normalHourlyRate ??
      entry?.NormalHourlyRate ??
      entry?.ratePerHour ??
      entry?.RatePerHour ??
      dayRecord?.ratePerHour ??
      staffData?.ratePerHour ??
      staffData?.RatePerHour ??
      detailReportData?.weekStats?.ratePerHour
    );
  };

  const resolveOvertimeRate = (entry, dayRecord = {}, staffData = null) => {
    return toNumber(
      entry?.overtimeHourlyRate ??
      entry?.OvertimeHourlyRate ??
      dayRecord?.overtimeHourlyRate ??
      staffData?.overTimeHourlyRate ??
      staffData?.OverTimeHourlyRate ??
      detailReportData?.weekStats?.overTimeHourlyRate ??
      resolveNormalRate(entry, dayRecord, staffData)
    );
  };

  const calculateDayStats = (entries, dayRecord = {}, staffData = null) => {
    return entries.reduce((acc, entry) => {
      const workedHours = toNumber(entry?.workedHours ?? entry?.Worked_Hours ?? entry?.WorkedHours ?? entry?.worked_hours ?? 0);
      let entryPay = toNumber(entry?.entryPay ?? entry?.EntryPay ?? entry?.entry_pay ?? 0);
      const isBreak = Boolean(entry?.isBreak);
      const isOvertime = Boolean(entry?.isOvertime);
      const breakType = (entry?.breakType || '').toString().toUpperCase();
      const normalRateResolved = resolveNormalRate(entry, dayRecord, staffData);
      const overtimeRateResolved = resolveOvertimeRate(entry, dayRecord, staffData);

      // Calculate entry pay if not set and not salary staff
      const isSalaryStaff = Boolean(selectedStaffDetail?.staff?.isSalaryStaff || selectedStaffDetail?.staff?.IsSalaryStaff);
      if (entryPay === 0 && !isSalaryStaff) {
        if (isBreak) {
          if (breakType !== 'UNPAID' && breakType !== 'UN-PAID') {
            entryPay = normalRateResolved * workedHours;
          }
        } else {
          entryPay = (isOvertime ? overtimeRateResolved : normalRateResolved) * workedHours;
        }
      }

      if (isBreak) {
        acc.totalBreakHours += workedHours;
        if (breakType === 'UNPAID' || breakType === 'UN-PAID') {
          acc.unpaidBreakHours += workedHours;
        }
        if (entryPay) {
          acc.totalPay += entryPay;
        }
      } else {
        acc.totalHours += workedHours;
        if (isOvertime) {
          acc.overtimeHours += workedHours;
        } else {
          acc.regularHours += workedHours;
        }
        acc.totalPay += entryPay;
      }

      return acc;
    }, {
      regularHours: 0,
      overtimeHours: 0,
      unpaidBreakHours: 0,
      totalBreakHours: 0,
      totalHours: 0,
      totalPay: 0
    });
  };

  // Calculate daily records for modal (no filters applied - show all entries)
  const modalDailyRecords = useMemo(() => {
    if (!detailReportData?.dailyRecords) return [];

    return detailReportData.dailyRecords
      .map((dayRecord) => {
        const entries = dayRecord.entries || [];

        if (!entries || entries.length === 0) {
          return null;
        }

        const dayStats = calculateDayStats(entries, dayRecord, selectedStaffDetail?.staff);

        const totalBreakHours = dayStats.totalBreakHours || dayRecord.totalBreakHours || 0;
        const unpaidBreakHours = dayStats.unpaidBreakHours || dayRecord.unpaidBreakHours || 0;
        const allowedBreakHours = dayRecord.allowedBreakHours || 0;
        const extraBreakHours = dayRecord.extraBreakHours || 0;
        
        const calculatedWorkingHours = dayStats.totalHours || 0;
        const paidBreakHours = totalBreakHours - unpaidBreakHours;
        const excessPaidBreakHours = Math.max(0, paidBreakHours - allowedBreakHours);
        
        const payableHours = Math.max(
          calculatedWorkingHours - unpaidBreakHours - excessPaidBreakHours,
          0
        );

        const regularHours = dayStats.regularHours || 0;
        const overtimeHours = dayStats.overtimeHours || 0;
        const workingHours = regularHours + overtimeHours;
        
        // Get rates using helper functions (checks entry, dayRecord, staff profile, weekStats)
        const normalRate = resolveNormalRate(entries[0] || {}, dayRecord, selectedStaffDetail?.staff);
        const overtimeRate = resolveOvertimeRate(entries[0] || {}, dayRecord, selectedStaffDetail?.staff);
        const avgHourlyRate = workingHours > 0 ? (dayStats.totalPay || 0) / workingHours : 0;
        const effectiveRate = normalRate || overtimeRate || avgHourlyRate || 0;
        
        const isSalaryStaff = Boolean(selectedStaffDetail?.staff?.isSalaryStaff || selectedStaffDetail?.staff?.IsSalaryStaff);
        
        // Calculate payable earnings: regular hours * normal rate + overtime hours * overtime rate
        let payableEarnings = 0;
        if (!isSalaryStaff) {
          if (overtimeHours > 0 && overtimeRate > 0) {
            // If there's overtime, calculate separately: regular hours at normal rate + overtime at overtime rate
            payableEarnings = (regularHours * normalRate) + (overtimeHours * overtimeRate);
          } else if (effectiveRate > 0) {
            // Use payable hours with effective rate
            payableEarnings = payableHours * effectiveRate;
          } else if (normalRate > 0) {
            // Fallback to normal rate if effective rate is 0
            payableEarnings = payableHours * normalRate;
          }
        }

        return {
          ...dayRecord,
          ...dayStats,
          entries,
          entryCount: entries.length,
          computedPayableHours: payableHours,
          computedPayableEarnings: payableEarnings,
          computedEffectiveRate: effectiveRate,
          extraBreakHours,
          allowedBreakHours
        };
      })
      .filter(Boolean);
  }, [detailReportData?.dailyRecords, selectedStaffDetail]);

  const modalWeekStats = useMemo(() => {
    if (!detailReportData?.weekStats && !modalDailyRecords.length) {
      return null;
    }

    if (!modalDailyRecords.length) {
      if (detailReportData?.weekStats) {
        return {
          daysWorked: detailReportData.weekStats.daysWorked || 0,
          regularHours: detailReportData.weekStats.regularHours || 0,
          overtimeHours: detailReportData.weekStats.overtimeHours || 0,
          unpaidBreakHours: detailReportData.weekStats.unpaidBreakHours || 0,
          totalBreakHours: detailReportData.weekStats.totalBreakHours || 0,
          extraBreakHours: detailReportData.weekStats.extraBreakHours || 0,
          totalHours: detailReportData.weekStats.totalHours || 0,
          totalEarnings: detailReportData.weekStats.totalEarnings || 0
        };
      }
      return {
        daysWorked: 0, regularHours: 0, overtimeHours: 0,
        unpaidBreakHours: 0, totalBreakHours: 0, extraBreakHours: 0,
        totalHours: 0, totalEarnings: 0
      };
    }

    const aggregate = modalDailyRecords.reduce((acc, dayRecord) => {
      acc.daysWorked += 1;
      acc.regularHours += dayRecord.regularHours || 0;
      acc.overtimeHours += dayRecord.overtimeHours || 0;
      acc.unpaidBreakHours += dayRecord.unpaidBreakHours || 0;
      acc.totalBreakHours += dayRecord.totalBreakHours || 0;
      acc.extraBreakHours += dayRecord.extraBreakHours || 0;
      acc.totalHours += dayRecord.totalHours || 0;
      acc.totalEarnings += dayRecord.computedPayableEarnings || dayRecord.totalPay || 0;
      return acc;
    }, {
      daysWorked: 0, regularHours: 0, overtimeHours: 0,
      unpaidBreakHours: 0, totalBreakHours: 0, extraBreakHours: 0,
      totalHours: 0, totalEarnings: 0
    });

    if (detailReportData?.weekStats) {
      const isSalaryStaff = Boolean(selectedStaffDetail?.staff?.isSalaryStaff || selectedStaffDetail?.staff?.IsSalaryStaff);
      return {
        ...detailReportData.weekStats,
        ...aggregate,
        daysWorked: aggregate.daysWorked,
        totalEarnings: isSalaryStaff
          ? (detailReportData.weekStats.totalEarnings ?? aggregate.totalEarnings)
          : aggregate.totalEarnings
      };
    }

    return aggregate;
  }, [detailReportData?.weekStats, modalDailyRecords, selectedStaffDetail]);

  const formatCurrency = (amount) => {
    try {
      // Use dynamic currency from company settings
      const normalizedCurrency = normalizeCurrencyCode(compCurrency);
      const locale = normalizedCurrency === 'USD' ? 'en-US' 
                   : normalizedCurrency === 'EUR' ? 'en-EU' 
                   : normalizedCurrency === 'PKR' ? 'en-PK'
                   : 'en-GB';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: normalizedCurrency
      }).format(amount || 0);
    } catch (error) {
      // Fallback to simple formatting with symbol if currency code is invalid
      console.error('Currency formatting error:', error);
      const symbol = getCurrencySymbol(compCurrency);
      return `${symbol}${(amount || 0).toFixed(2)}`;
    }
  };

  const staffOptions = React.useMemo(() => {
    console.log('Building staff options from:', allStaffList); // Debug log
    
    const options = [
      { value: '', label: 'All Staff' },
      ...allStaffList.map(staff => {
        console.log('Mapping staff:', staff); // Debug each staff member
        return {
          value: staff.userName || staff.UserName,
          label: `${staff.firstName || ''} ${staff.lastName || ''}`.trim() || staff.email || staff.userName || 'Unknown'
        };
      })
    ];
    
    console.log('Final staff options:', options); // Debug log
    return options;
  }, [allStaffList]);

  const filterCardStyle = React.useMemo(() => ({
    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
    borderRadius: '16px',
    border: 'none',
    boxShadow: isDarkMode
      ? '0 20px 36px rgba(0, 0, 0, 0.55)'
      : `0 22px 38px ${themeColor}40`,
    position: 'relative',
    overflow: 'hidden',
  }), [themeColor, isDarkMode]);

  const dropdownContainerStyle = React.useMemo(() => ({
    borderRadius: '14px',
    padding: '8px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.9)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: isDarkMode
      ? '0 12px 28px rgba(0, 0, 0, 0.35)'
      : `0 10px 20px ${themeColor}33`,
    transition: 'all 0.3s ease',
  }), [isDarkMode, themeColor]);

  const dropdownLabelStyle = {
    color: isDarkMode ? 'rgba(226, 232, 240, 0.85)' : 'rgba(15, 23, 42, 0.7)',
    fontSize: '0.85rem',
    fontWeight: 600,
  };

  const searchIconColor = isDarkMode ? 'rgba(148, 163, 184, 0.68)' : 'rgba(34, 56, 78, 0.75)';

  const totalSummary = staffReports.reduce((acc, { report }) => {
    if (report?.WeekStats) {
      acc.totalHours += report.WeekStats.TotalHours || 0;
      acc.regularHours += report.WeekStats.RegularHours || 0;
      acc.overtimeHours += report.WeekStats.OvertimeHours || 0;
      acc.totalBreakHours += report.WeekStats.TotalBreakHours || 0;
      acc.totalEarnings += report.WeekStats.TotalEarnings || 0;
      acc.daysWorked += report.WeekStats.DaysWorked || 0;
    }
    return acc;
  }, { totalHours: 0, regularHours: 0, overtimeHours: 0, totalBreakHours: 0, totalEarnings: 0, daysWorked: 0 });


  return (
    <div>
      {loading && <Preloader />}
      
      {/* Add style for placeholder colors */}
      <style>
        {`
          .staff-report-search-input::placeholder {
            color: ${isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'} !important;
          }
        `}
      </style>
      
      {/* Breadcrumb */}
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><a href="#/">Home</a></li>
        <li className="breadcrumb-item"><a href="#/team">Staff</a></li>
        <li className="breadcrumb-item active">Staff Shift Report</li>
      </ol>
      <h1 className="page-header">Staff Shift Report</h1>

      {/* Filters */}
      <div 
        className="card border-0 mb-3" 
        style={{ 
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: '12px',
          boxShadow: isDarkMode
            ? '0 4px 16px rgba(0, 0, 0, 0.3)'
            : `0 4px 16px ${themeColor}30`,
          position: 'relative',
          zIndex: 10
        }}
      >
        <div className="card-body py-3 px-3">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            {/* Search Input */}
            <div style={{ flex: '1 1 auto', minWidth: '200px', maxWidth: '350px' }}>
              <div 
                style={{
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.4)',
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <input
                  type="text"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by name, phone..."
                  className="staff-report-search-input"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: isDarkMode ? 'white' : 'black',
                    width: '100%',
                    outline: 'none',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {isMain && locationOptions.length > 1 && (
              <div style={{ flex: '1 1 auto', minWidth: '180px', maxWidth: '250px' }}>
                <div 
                  style={{
                    backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.4)',
                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                    padding: '4px 8px'
                  }}
                >
                  <Select
                    {...defaultSelectPortalProps}
                    value={location}
                    onChange={handleLocationChange}
                    options={locationOptions}
                    isSearchable={false}
                    isLoading={loadingLocations}
                    styles={{
                      ...dropdownStyles,
                      control: (base, state) => ({
                        ...dropdownStyles.control(base, state),
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                        minHeight: '32px'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: isDarkMode ? 'white' : 'black',
                        fontSize: '14px'
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                        fontSize: '14px'
                      }),
                      input: (base) => ({
                        ...base,
                        color: isDarkMode ? 'white' : 'black'
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        padding: '4px',
                        color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
                      }),
                      indicatorSeparator: () => ({
                        display: 'none'
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                    placeholder="Location"
                    className="filter-dropdown"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>
            )}

            {/* Staff Dropdown */}
            <div style={{ flex: '1 1 auto', minWidth: '180px', maxWidth: '250px' }}>
              <div 
                style={{
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.4)',
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  padding: '4px 8px'
                }}
              >
                <Select
                  {...defaultSelectPortalProps}
                  value={selectedStaff}
                  onChange={handleStaffChange}
                  options={staffOptions}
                  styles={{
                    ...dropdownStyles,
                    control: (base, state) => ({
                      ...dropdownStyles.control(base, state),
                      backgroundColor: 'transparent',
                      border: 'none',
                      boxShadow: 'none',
                      minHeight: '32px'
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: isDarkMode ? 'white' : 'black',
                      fontSize: '14px'
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)',
                      fontSize: '14px'
                    }),
                    input: (base) => ({
                      ...base,
                      color: isDarkMode ? 'white' : 'black'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      padding: '4px',
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
                    }),
                    indicatorSeparator: () => ({
                      display: 'none'
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999
                    })
                  }}
                  placeholder="Name"
                  className="filter-dropdown"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                />
              </div>
            </div>

            {/* Date Range Picker */}
            <div style={{ flex: '1 1 auto', minWidth: '200px', maxWidth: '280px' }}>
              <div 
                style={{
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.4)',
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                  padding: '4px 8px'
                }}
              >
                <SimpleDateRangePicker
                  startDate={startDate && moment(startDate).isValid() ? startDate : null}
                  endDate={endDate && moment(endDate).isValid() ? endDate : null}
                  onChange={handleDateRangeChange}
                  placeholder="Desc"
                  className="filter-dropdown"
                />
              </div>
            </div>

            {/* Filter Button */}
            <button
              type="button"
              onClick={clearFilters}
              style={{
                backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: 'white',
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
                flex: '0 0 auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.2)';
              }}
              title="Clear filters"
            >
              <i className="fa fa-times"></i>
              Filter
            </button>
          </div>
        </div>
      </div>

      {/* Staff Report Table */}
      <div className="card border-0" style={{ position: 'relative', zIndex: 1 }}>
        <div className="card-body p-0">
          <div className="table-responsive" style={{ position: 'relative', zIndex: 1 }}>
            <table className="table table-hover mb-0">
              <thead 
                className="bg-light" 
                style={{ 
                  position: 'sticky', 
                  top: 0, 
                  zIndex: 3,
                  backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa'
                }}
              >
                <tr>
                  <th className="pt-3 pb-3">Staff Member</th>
                  <th className="pt-3 pb-3">Role</th>
                  <th className="pt-3 pb-3 text-center">Days Worked</th>
                  <th className="pt-3 pb-3 text-center">Total Hours</th>
                  <th className="pt-3 pb-3 text-center">Overtime Hours</th>
                  <th className="pt-3 pb-3 text-center">Break Hours</th>
                  <th className="pt-3 pb-3 text-center">Rate</th>
                  <th className="pt-3 pb-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {staffReports.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <i className="fa fa-users fa-3x text-muted mb-3 d-block"></i>
                      <h5>No Staff Shift Reports Found</h5>
                      <p className="text-muted">Try adjusting your filters</p>
                    </td>
                  </tr>
                ) : (
                  staffReports.map(({ staff, report }) => {
                    console.log('Rendering staff row:', staff); // Debug log
                    
                    // Check if staff is salary staff - check multiple possible field names
                    const isSalaryStaffRaw = pickValueIgnoreCase(staff, [
                      'isSalaryStaff',
                      'IsSalaryStaff',
                      'IS_SALARY_STAFF',
                      'isSalaryStaff'
                    ]);
                    const isSalaryStaff = parseBooleanFlag(isSalaryStaffRaw);
                    
                    console.log('Salary Staff Check:', {
                      raw: isSalaryStaffRaw,
                      parsed: isSalaryStaff,
                      staff: staff.UserName || staff.userName
                    });
                    
                    // Get salary type (Weekly or Monthly)
                    const salaryType = pickValueIgnoreCase(staff, [
                      'salaryType',
                      'SalaryType',
                      'salary_type',
                      'SALARY_TYPE'
                    ]);
                    
                    // Get salary amount - handle both number and string
                    const salary = pickValueIgnoreCase(staff, [
                      'salary',
                      'Salary',
                      'SALARY'
                    ]);
                    let salaryAmount = null;
                    if (salary !== null && salary !== undefined && salary !== '') {
                      const parsed = Number(salary);
                      salaryAmount = !isNaN(parsed) ? parsed : null;
                    }
                    
                    console.log('Salary Info:', {
                      isSalaryStaff,
                      salaryType,
                      salary,
                      salaryAmount,
                      staff: staff.UserName || staff.userName,
                      rawStaff: staff
                    });
                    
                    // Get hourly rate
                    const hourlyRate = pickValueIgnoreCase(staff, [
                      'ratePerHour',
                      'RatePerHour',
                      'rate_per_hour',
                      'RATE_PER_HOUR'
                    ]);
                    const hourlyRateValue = hourlyRate !== null && hourlyRate !== undefined && hourlyRate !== '' ? Number(hourlyRate) : null;
                    
                    // Calculate average hourly rate (only for hourly staff)
                    const regularHours = report?.WeekStats?.RegularHours || 0;
                    const overtimeHours = report?.WeekStats?.OvertimeHours || 0;
                    const workingHours = regularHours + overtimeHours; // Excludes break hours
                    const totalEarnings = report?.WeekStats?.TotalEarnings || 0;
                    const avgHourlyRate = !isSalaryStaff && workingHours > 0 ? (totalEarnings / workingHours) : (hourlyRateValue || 0);
                    
                    return (
                    <tr key={staff.UserName || staff.userName}>
                      <td className="align-middle">
                        <div className="fw-bold">
                          {staff.FirstName && staff.LastName 
                            ? `${staff.FirstName} ${staff.LastName}` 
                            : staff.firstName && staff.lastName
                            ? `${staff.firstName} ${staff.lastName}`
                            : staff.UserName || 'N/A'}
                        </div>
                      </td>
                      <td className="align-middle">
                        <span className="text-muted">
                          {staff.RoleName || staff.roleName || 'N/A'}
                        </span>
                      </td>
                      <td className="align-middle text-center">
                        {report?.WeekStats?.DaysWorked || 0}
                      </td>
                      <td className="align-middle text-center">
                        {report?.WeekStats?.TotalHours?.toFixed(1) || '0.0'} hrs
                      </td>
                      <td className="align-middle text-center">
                        {report?.WeekStats?.OvertimeHours?.toFixed(1) || '0.0'} hrs
                      </td>
                      <td className="align-middle text-center">
                        <span className={report?.WeekStats?.TotalBreakHours > 0 ? 'text-warning fw-semibold' : ''}>
                          {report?.WeekStats?.TotalBreakHours?.toFixed(1) || '0.0'} hrs
                        </span>
                      </td>
                      <td className="align-middle text-center">
                        <span className="text-info fw-semibold">
                          {isSalaryStaff 
                            ? (salaryAmount !== null && salaryAmount !== undefined && !isNaN(salaryAmount)
                                ? `${formatCurrency(salaryAmount)}/${(salaryType || 'Monthly').toLowerCase()}`
                                : 'Salary not set')
                            : (avgHourlyRate > 0 ? `${formatCurrency(avgHourlyRate)}/hr` : (hourlyRateValue > 0 ? `${formatCurrency(hourlyRateValue)}/hr` : '-'))
                          }
                        </span>
                      </td>
                      <td className="align-middle text-center">
                        <div className="d-flex gap-2 justify-content-center">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleViewDetails(staff.UserName || staff.userName)}
                            title="View Shift Report Details"
                            style={{
                              width: '36px',
                              height: '36px',
                              padding: '0',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <i className="fa fa-eye"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleOpenPayoutModal(staff)}
                            title="Generate Payout"
                            style={{
                              width: '36px',
                              height: '36px',
                              padding: '0',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <i className="fa fa-plus"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => handleViewPayoutHistory(staff.UserName || staff.userName)}
                            title="View Payout History"
                            style={{
                              width: '36px',
                              height: '36px',
                              padding: '0',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <i className="fa fa-wallet"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Staff Detail Modal */}
      <CustomModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedStaffDetail(null);
          setDetailReportData(null);
          setExpandedDays(new Set());
        }}
        title={
          <div className="d-flex align-items-center justify-content-between w-100">
            <span>
              {selectedStaffDetail ? 
                `${selectedStaffDetail.staff.FirstName || selectedStaffDetail.staff.firstName || ''} ${selectedStaffDetail.staff.LastName || selectedStaffDetail.staff.lastName || ''} - Shift Report` 
                : 'Staff Shift Report'}
            </span>
            {modalDailyRecords && modalDailyRecords.length > 0 && (
              <span className="badge bg-primary ms-2">
                {modalDailyRecords.length} {modalDailyRecords.length === 1 ? 'day' : 'days'}
              </span>
            )}
          </div>
        }
        modalSize="modal-xl"
        showButton={false}
        showSubmitButton={false}
        showCancelButton={false}
        maxHeight="90vh"
      >
        {detailLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : selectedStaffDetail && detailReportData ? (
          <div>
            {/* Daily Records Table */}
            {modalDailyRecords && modalDailyRecords.length > 0 ? (
              <div>
                <div className="table-responsive">
                  <table className="table table-hover table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th className="text-center">Overtime</th>
                        <th className="text-center">Break</th>
                        <th className="text-center">Payable</th>
                        <th className="text-center">Rate</th>
                        <th className="text-center">Earnings</th>
                        <th className="text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalDailyRecords.map((dayRecord) => {
                        const isExpanded = expandedDays.has(dayRecord.date);
                        const totalBreakHours = dayRecord.totalBreakHours || dayRecord.unpaidBreakHours || 0;
                        const extraBreakHours = dayRecord.extraBreakHours || 0;
                        const allowedBreakHours = dayRecord.allowedBreakHours || 0;
                        const payableHours = dayRecord.computedPayableHours ?? dayRecord.totalHours ?? 0;
                        
                        const regularHours = dayRecord.regularHours || 0;
                        const overtimeHours = dayRecord.overtimeHours || 0;
                        const workingHours = regularHours + overtimeHours;
                        const normalRate = dayRecord.entries.find(e => !e.isOvertime && e.normalHourlyRate > 0)?.normalHourlyRate || 0;
                        const overtimeRate = dayRecord.entries.find(e => e.isOvertime && e.overtimeHourlyRate > 0)?.overtimeHourlyRate || 0;
                        const avgHourlyRate = dayRecord.computedEffectiveRate ?? (workingHours > 0 ? dayRecord.totalPay / workingHours : 0);
                        
                        return (
                          <React.Fragment key={dayRecord.date}>
                            <tr 
                              onClick={() => toggleDayExpansion(dayRecord.date)}
                              style={{ cursor: 'pointer' }}
                              className={isExpanded ? 'table-active' : ''}
                            >
                              <td>
                                <div className="d-flex align-items-center">
                                  <span className="fw-semibold">
                                    {moment(dayRecord.date).format('ddd, MMM DD, YYYY')}
                                  </span>
                                  {dayRecord.totalHours > 24 && (
                                    <span className="badge bg-dark ms-2">
                                      <i className="fa fa-moon me-1"></i>
                                      Overnight
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="text-center">
                                {overtimeHours > 0 ? (
                                  <span className="fw-semibold text-purple">
                                    {overtimeHours.toFixed(2)} hrs
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td className="text-center">
                                {totalBreakHours > 0 ? (
                                  <span className="fw-semibold text-warning">
                                    {totalBreakHours.toFixed(2)} hrs
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td className="text-center">
                                <span className="fw-bold text-primary">
                                  {payableHours.toFixed(2)} hrs
                                </span>
                              </td>
                              <td className="text-center">
                                <div className="d-flex flex-column align-items-center">
                                  {normalRate > 0 && (
                                    <span className="fw-semibold text-info">
                                      {formatCurrency(normalRate)}/hr
                                    </span>
                                  )}
                                  {overtimeRate > 0 && (
                                    <span className="fw-semibold text-purple">
                                      {formatCurrency(overtimeRate)}/hr OT
                                    </span>
                                  )}
                                  {!normalRate && !overtimeRate && avgHourlyRate > 0 && (
                                    <span className="fw-semibold text-info">
                                      {formatCurrency(avgHourlyRate)}/hr
                                    </span>
                                  )}
                                  {!normalRate && !overtimeRate && avgHourlyRate === 0 && (
                                    <span className="text-muted">-</span>
                                  )}
                                </div>
                              </td>
                              <td className="text-center">
                                <span className="fw-bold text-primary">
                                  {formatCurrency(dayRecord.computedPayableEarnings ?? dayRecord.totalPay)}
                                </span>
                              </td>
                              <td className="text-center">
                                <div className="d-flex align-items-center justify-content-center gap-2">
                                  <span>{dayRecord.entries.length}</span>
                                  <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'} text-muted`}></i>
                                </div>
                              </td>
                            </tr>
                            
                            {/* Expanded Entries Row */}
                            {isExpanded && (
                              <tr>
                                <td colSpan="7" className="p-0">
                                  <div className="p-3 bg-light">
                                    <h6 className="mb-3">Time Entries</h6>
                                    <div className="table-responsive">
                                      <table className="table table-sm table-bordered mb-0">
                                        <thead className="table-secondary">
                                          <tr>
                                            <th></th>
                                            <th>Time</th>
                                            <th>Type</th>
                                            <th>Status</th>
                                            <th className="text-center">Hours</th>
                                            <th className="text-center">Rate</th>
                                            <th className="text-center">Pay</th>
                                            <th>Device</th>
                                            <th>Notes</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {dayRecord.entries.map((entry) => {
                                            const isEntryPaid = getEntryPaidStatus(entry);
                                            
                                            return (
                                              <tr key={entry.timeTracking_ID}>
                                                <td className="text-center">
                                                  <i className={getEntryIcon(entry)}></i>
                                                </td>
                                                <td>
                                                  <span className="fw-semibold">
                                                    {entry.startTime} - {entry.endTime || 'Active'}
                                                  </span>
                                                </td>
                                                <td>
                                                  {getEntryTypeLabel(entry)}
                                                </td>
                                                <td>
                                                  <div>
                                                    {isEntryPaid ? 'Paid' : 'Unpaid'}
                                                    {entry.isLate && <span className="ms-2">Late</span>}
                                                    {entry.isEarly && <span className="ms-2">Early</span>}
                                                    {entry.isManualEntry && <span className="ms-2">Manual</span>}
                                                    {entry.isOvernightShift && <span className="ms-2">Overnight</span>}
                                                    {entry.isActiveShift && <span className="ms-2">Active</span>}
                                                  </div>
                                                </td>
                                                <td className="text-center">
                                                  <span className="fw-semibold">
                                                    {entry.workedHours.toFixed(2)} hrs
                                                  </span>
                                                </td>
                                                <td className="text-center">
                                                  {entry.isOvertime && entry.overtimeHourlyRate > 0 ? (
                                                    <span className="text-purple fw-semibold">
                                                      {formatCurrency(entry.overtimeHourlyRate)}/hr
                                                    </span>
                                                  ) : entry.normalHourlyRate > 0 ? (
                                                    <span className="text-info fw-semibold">
                                                      {formatCurrency(entry.normalHourlyRate)}/hr
                                                    </span>
                                                  ) : (
                                                    <span className="text-muted">-</span>
                                                  )}
                                                </td>
                                                <td className="text-center">
                                                  {entry.entryPay > 0 ? (
                                                    <span className="fw-bold text-success">
                                                      {formatCurrency(entry.entryPay)}
                                                    </span>
                                                  ) : (
                                                    <span className="text-muted">-</span>
                                                  )}
                                                </td>
                                                <td>
                                                  {entry.deviceName ? (
                                                    <span>
                                                      <i className="fa fa-mobile me-1"></i>
                                                      {entry.deviceName}
                                                    </span>
                                                  ) : (
                                                    <span className="text-muted">-</span>
                                                  )}
                                                </td>
                                                <td>
                                                  {entry.notes ? (
                                                    <small className="text-muted">
                                                      <i className="fa fa-note-sticky me-1"></i>
                                                      {entry.notes}
                                                    </small>
                                                  ) : (
                                                    <span className="text-muted">-</span>
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-5">
                <i className="fa fa-calendar-xmark fa-3x text-muted mb-3 d-block"></i>
                <h5>No Daily Records Found</h5>
                <p className="text-muted">No work records available for the selected date range</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-5">
            <i className="fa fa-exclamation-triangle fa-3x text-muted mb-3 d-block"></i>
            <h5>No Data Available</h5>
          </div>
        )}
      </CustomModal>

      {/* Generate Payout Modal */}
      <CustomModal
        isOpen={showPayoutModal}
        onClose={() => {
          setShowPayoutModal(false);
          setPayoutStaff(null);
          setRawUnpaidHours(null);
          setStaffProfile(null);
          setStaffLeaves([]);
          setSelectedLeaves(new Set());
        }}
        title={payoutStaff ? `Generate Payout - ${payoutStaff.FirstName || payoutStaff.firstName || ''} ${payoutStaff.LastName || payoutStaff.lastName || ''}` : 'Generate Payout'}
        modalSize="modal-lg"
        showButton={true}
        submitButtonText="Generate"
        onSubmit={handleGeneratePayout}
        submitButtonDisabled={!unpaidHours || unpaidHours.payableHours <= 0}
      >
        <div>
          {/* Unpaid Hours Summary */}
          {loadingUnpaidHours ? (
            <div className="alert alert-info mb-4">
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Loading payout information...
              </div>
            </div>
          ) : unpaidHours && (
            <div className="alert alert-info mb-4">
              <h6 className="mb-3">Unpaid Hours Summary</h6>
              <div className="row g-3">
                <div className="col-md-3">
                  <div><strong>Payable Hours:</strong></div>
                  <div className="fs-5 fw-bold text-primary">{unpaidHours.payableHours.toFixed(1)} hrs</div>
                  <div className="text-muted small mt-1">
                    Recorded: {unpaidHours.totalWorkingHours.toFixed(1)} hrs
                  </div>
                  {unpaidHours.unpaidBreakAdjustmentHours > 0 && (
                    <div className="text-muted small text-danger">
                      -{unpaidHours.unpaidBreakAdjustmentHours.toFixed(1)} hrs unpaid breaks
                    </div>
                  )}
                  {(unpaidHours.extraBreakHours || 0) > 0 && (
                    <div className="text-muted small text-danger">
                      -{(unpaidHours.extraBreakHours || 0).toFixed(1)} hrs extra breaks
                    </div>
                  )}
                  <div className="text-muted small mt-1">
                    Regular: {unpaidHours.payableRegularHours.toFixed(1)} hrs
                    {((unpaidHours.adjustedPaidBreakHours || 0) > 0) && (
                      <span className="text-success"> + {(unpaidHours.adjustedPaidBreakHours || 0).toFixed(1)} paid breaks</span>
                    )}
                  </div>
                  <div className="text-muted small">
                    Overtime: {unpaidHours.payableOvertimeHours.toFixed(1)} hrs
                  </div>
                </div>
                <div className="col-md-3">
                  <div><strong>Break Hours:</strong></div>
                  <div className="fs-5 fw-bold text-warning">
                    {(unpaidHours.totalBreakHours || 0).toFixed(1)} hrs
                  </div>
                  {((unpaidHours.adjustedPaidBreakHours || 0) > 0) && (
                    <div className="text-success small mt-1">
                      Paid: {(unpaidHours.adjustedPaidBreakHours || 0).toFixed(1)} hrs
                      {((unpaidHours.extraBreakHours || 0) > 0) && (
                        <span className="text-muted"> ({(unpaidHours.paidBreakHours || 0).toFixed(1)} - {(unpaidHours.extraBreakHours || 0).toFixed(1)} extra)</span>
                      )}
                    </div>
                  )}
                  {(unpaidHours.unpaidBreakHours || 0) > 0 && (
                    <div className="text-muted small">
                      Unpaid: {(unpaidHours.unpaidBreakHours || 0).toFixed(1)} hrs
                    </div>
                  )}
                  {(unpaidHours.extraBreakHours || 0) > 0 && (
                    <div className="text-danger small">
                      Extra: {(unpaidHours.extraBreakHours || 0).toFixed(1)} hrs (deducted)
                    </div>
                  )}
                </div>
                <div className="col-md-3">
                  <div><strong>Earnings Breakdown:</strong></div>
                  <div className="small">
                    <div>Regular: {formatCurrency(unpaidHours.payableRegularEarnings || 0)}</div>
                    <div>Overtime: {formatCurrency(unpaidHours.payableOvertimeEarnings || 0)}</div>
                    {((unpaidHours.adjustedPaidBreakHours || 0) > 0) && (
                      <div className="text-muted small">
                        (includes {(unpaidHours.adjustedPaidBreakHours || 0).toFixed(1)} hrs paid breaks)
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div><strong>Total Earnings:</strong></div>
                  <div className="fs-5 fw-bold text-success">
                    {formatCurrency(unpaidHours.payableTotalEarnings || 0)}
                  </div>
                  {((unpaidHours.adjustedPaidBreakHours || 0) > 0) && (
                    <div className="text-success small mt-1">
                      Includes paid breaks
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Leave Information */}
          {staffLeaves.length > 0 && (
            staffProfile ? (
              <div className={`alert mb-4 ${staffProfile.isSalaryStaff ? 'alert-warning' : 'alert-success'}`}>
                <h6 className="mb-3">
                  <i className="fa fa-calendar-times me-2"></i>
                  Leave Records ({staffLeaves.length})
                </h6>
                {staffProfile.isSalaryStaff ? (
                  <div>
                    <p className="mb-3">
                      <i className="fa fa-info-circle me-1"></i>
                      Select which leaves to process in the salary calculation. Paid leaves are already included in base salary. Unpaid leaves will be deducted from the total.
                    </p>
                    <div className="row g-2">
                      {staffLeaves.map((leave) => {
                        const isPaidLeave = leave.IsPaid;
                        const isSelected = selectedLeaves.has(leave.SL_ID);
                        const leaveDays = moment(leave.EndDate).diff(moment(leave.StartDate), 'days') + 1;
                        const daysInPeriod = moment(payoutForm.endDate).diff(moment(payoutForm.startDate), 'days') + 1;
                        const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);
                        const proRatedAmount = dailySalary * leaveDays;
                        
                        // For paid leaves, always show as selected and disabled
                        const isAlwaysSelected = isPaidLeave;
                        
                        return (
                          <div key={leave.SL_ID} className="col-12">
                            <div className="card border">
                              <div className="card-body p-3">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={isAlwaysSelected || isSelected}
                                    disabled={isPaidLeave}
                                    onChange={(e) => {
                                      // Only allow changes for unpaid leaves
                                      if (!isPaidLeave) {
                                        const newSelected = new Set(selectedLeaves);
                                        if (e.target.checked) {
                                          newSelected.add(leave.SL_ID);
                                        } else {
                                          newSelected.delete(leave.SL_ID);
                                        }
                                        setSelectedLeaves(newSelected);
                                      }
                                    }}
                                    id={`leave-${leave.SL_ID}`}
                                  />
                                  <label className="form-check-label w-100" htmlFor={`leave-${leave.SL_ID}`}>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <strong>{leave.LeaveType || 'Leave'}</strong>
                                        <span className={`badge ms-2 ${leave.IsPaid ? 'bg-success' : 'bg-secondary'}`}>
                                          {leave.IsPaid ? 'Paid' : 'Unpaid'}
                                        </span>
                                        <div className="text-muted small mt-1">
                                          {moment(leave.StartDate).format('MMM DD')} - {moment(leave.EndDate).format('MMM DD, YYYY')} ({leaveDays} day{leaveDays !== 1 ? 's' : ''})
                                        </div>
                                        {leave.Reason && (
                                          <div className="text-muted small">
                                            <i className="fa fa-note-sticky me-1"></i>
                                            {leave.Reason}
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-end">
                                        <div className={`fw-bold ${isPaidLeave ? 'text-success' : (isSelected ? 'text-danger' : 'text-muted')}`}>
                                          {isPaidLeave ? (
                                            formatCurrency(proRatedAmount) + ' (Included)'
                                          ) : isSelected ? (
                                            `-${formatCurrency(proRatedAmount)}`
                                          ) : (
                                            formatCurrency(proRatedAmount)
                                          )}
                                        </div>
                                        <div className="text-muted small">
                                          {isPaidLeave ? 'Always included in salary' : (isSelected ? 'Will be deducted' : 'Unpaid leave')}
                                        </div>
                                      </div>
                                    </div>
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3">
                      <div className="mb-2">
                        <strong>Paid Leaves Amount:</strong>{' '}
                        <span className="fw-bold text-success">
                          {(() => {
                            const paidLeavesList = staffLeaves.filter(l => l.IsPaid);
                            const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);
                            const totalPaidAmount = paidLeavesList.reduce((sum, leave) => {
                              const leaveDays = moment(leave.EndDate).diff(moment(leave.StartDate), 'days') + 1;
                              const proRatedAmount = dailySalary * leaveDays;
                              return sum + proRatedAmount;
                            }, 0);
                            return formatCurrency(totalPaidAmount);
                          })()}
                        </span>
                        <span className="text-muted small ms-2">(Always included in salary)</span>
                      </div>
                      <div>
                        <strong>Total Leave Adjustment:</strong>{' '}
                        <span className={`fw-bold ${(() => {
                          // Include all paid leaves (always selected) + selected unpaid leaves
                          const paidLeavesList = staffLeaves.filter(l => l.IsPaid);
                          const selectedUnpaidLeaves = staffLeaves.filter(l => !l.IsPaid && selectedLeaves.has(l.SL_ID));
                          const allSelectedLeaves = [...paidLeavesList, ...selectedUnpaidLeaves];
                          const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);
                          const total = allSelectedLeaves.reduce((sum, leave) => {
                            const leaveDays = moment(leave.EndDate).diff(moment(leave.StartDate), 'days') + 1;
                            const proRatedAmount = dailySalary * leaveDays;
                            return sum + (leave.IsPaid ? 0 : -proRatedAmount);
                          }, 0);
                          return total >= 0 ? 'text-success' : 'text-danger';
                        })()}`}>
                          {(() => {
                            // Include all paid leaves (always selected) + selected unpaid leaves
                            const paidLeavesList = staffLeaves.filter(l => l.IsPaid);
                            const selectedUnpaidLeaves = staffLeaves.filter(l => !l.IsPaid && selectedLeaves.has(l.SL_ID));
                            const allSelectedLeaves = [...paidLeavesList, ...selectedUnpaidLeaves];
                            const dailySalary = (staffProfile.salary || 0) / (staffProfile.salaryType === 'Monthly' ? 30 : 7);
                            const total = allSelectedLeaves.reduce((sum, leave) => {
                              const leaveDays = moment(leave.EndDate).diff(moment(leave.StartDate), 'days') + 1;
                              const proRatedAmount = dailySalary * leaveDays;
                              return sum + (leave.IsPaid ? 0 : -proRatedAmount);
                            }, 0);
                            return formatCurrency(Math.abs(total)) + (total < 0 ? ' (deduction)' : '');
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="mb-2">
                      <i className="fa fa-info-circle me-1"></i>
                      Paid leave hours will be automatically included in the payout calculation.
                    </p>
                    <div className="row g-2">
                      {staffLeaves.filter(l => l.IsPaid).map((leave) => {
                        const hourlyRate = staffProfile.ratePerHour || 0;
                        const leaveEarnings = (leave.TotalHours || 0) * hourlyRate;
                        
                        return (
                          <div key={leave.SL_ID} className="col-md-6">
                            <div className="card border border-success">
                              <div className="card-body p-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <strong>{leave.LeaveType || 'Leave'}</strong>
                                    <span className="badge bg-success ms-2">Paid</span>
                                    <div className="text-muted small">
                                      {moment(leave.StartDate).format('MMM DD')} - {moment(leave.EndDate).format('MMM DD')}
                                    </div>
                                    <div className="text-muted small">
                                      {(leave.TotalHours || 0).toFixed(1)} hrs
                                    </div>
                                  </div>
                                  <div className="text-end">
                                    <div className="text-success fw-bold">
                                      +{formatCurrency(leaveEarnings)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3">
                      <strong>Total Leave Earnings:</strong>{' '}
                      <span className="text-success fw-bold">
                        {(() => {
                          const paidLeaves = staffLeaves.filter(l => l.IsPaid);
                          const hourlyRate = staffProfile.ratePerHour || 0;
                          const total = paidLeaves.reduce((sum, leave) => {
                            return sum + ((leave.TotalHours || 0) * hourlyRate);
                          }, 0);
                          return formatCurrency(total);
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="alert alert-info mb-4">
                <p className="text-muted mb-0">
                  <i className="fa fa-spinner fa-spin me-1"></i>
                  Loading staff information...
                </p>
              </div>
            )
          )}

          <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Pay Period</label>
                    <SimpleDateRangePicker
                      startDate={payoutForm.startDate && moment(payoutForm.startDate).isValid() ? payoutForm.startDate : null}
                      endDate={payoutForm.endDate && moment(payoutForm.endDate).isValid() ? payoutForm.endDate : null}
                      onChange={(update) => {
                        const [newStart, newEnd] = update;
                        setPayoutForm({ ...payoutForm, startDate: newStart, endDate: newEnd });
                        // Preserve paid leaves, only reset unpaid leaves selection
                        const paidLeaveIds = new Set(
                          staffLeaves.filter(l => l.IsPaid).map(l => l.SL_ID)
                        );
                        setSelectedLeaves(paidLeaveIds);
                      }}
                      placeholder="Select period"
                    />
                  </div>
            <div className="col-md-6">
              <label className="form-label">Payout Type</label>
              <select
                className="form-select"
                value={payoutForm.payoutType}
                onChange={(e) => setPayoutForm({ ...payoutForm, payoutType: e.target.value })}
                style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  borderColor: isDarkMode ? '#374151' : '#dee2e6'
                }}
              >
                <option value="WEEKLY">Weekly</option>
                <option value="BI_WEEKLY">Bi-Weekly</option>
                <option value="MONTHLY">Monthly</option>
                <option value="CUSTOM">Custom</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Bonus Amount</label>
              <input
                type="number"
                className="form-control"
                value={payoutForm.bonusAmount}
                onChange={(e) => setPayoutForm({ ...payoutForm, bonusAmount: e.target.value })}
                placeholder="0.00"
                step="0.01"
                style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  borderColor: isDarkMode ? '#374151' : '#dee2e6'
                }}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Deduction Amount</label>
              <input
                type="number"
                className="form-control"
                value={payoutForm.deductionAmount}
                onChange={(e) => setPayoutForm({ ...payoutForm, deductionAmount: e.target.value })}
                placeholder="0.00"
                step="0.01"
                style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  borderColor: isDarkMode ? '#374151' : '#dee2e6'
                }}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Tax Amount</label>
              <input
                type="number"
                className="form-control"
                value={payoutForm.taxAmount}
                onChange={(e) => setPayoutForm({ ...payoutForm, taxAmount: e.target.value })}
                placeholder="0.00"
                step="0.01"
                style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  borderColor: isDarkMode ? '#374151' : '#dee2e6'
                }}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Notes</label>
              <textarea
                className="form-control"
                value={payoutForm.notes}
                onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                rows="3"
                placeholder="Enter any notes..."
                style={{
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  color: isDarkMode ? '#ffffff' : '#000000',
                  borderColor: isDarkMode ? '#374151' : '#dee2e6'
                }}
              ></textarea>
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default StaffReport;
